// src/components/team/AddPlayerModal.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  PlusIcon,
  TrophyIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Player } from '../../types';
import apiService from '../../services/api';
import { bySeasonAverageDesc, isElite, seasonAverage } from '../../utils/playerSeason';

interface AddPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwapPlayer: (playerAddId: number, playerDropId?: number) => Promise<void>;
  currentRoster: Player[];
  leagueId?: number;
  teamId?: number;
}

type Step = 'browse' | 'confirm-drop';

export default function AddPlayerModal({
  isOpen,
  onClose,
  onSwapPlayer,
  currentRoster,
  leagueId,
  teamId,
}: AddPlayerModalProps) {
  const [step, setStep] = useState<Step>('browse');
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState<'all' | 'K' | 'P'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'fantasy_points_avg' | 'name'>('fantasy_points_avg');

  const [players, setPlayers] = useState<Player[]>([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState<string | null>(null);

  const [pendingAdd, setPendingAdd] = useState<Player | null>(null);
  const [selectedDropId, setSelectedDropId] = useState<number | null>(null);
  const [swapping, setSwapping] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);

  const rosterFull = currentRoster.length >= 6;

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlayers = useCallback(() => {
    if (!isOpen) return;
    setPlayersLoading(true);
    setPlayersError(null);
    const fetch = leagueId
      ? apiService.getAvailablePlayers(leagueId)
      : apiService.getPlayers({ limit: 200 });
    fetch
      .then(setPlayers)
      .catch(() => setPlayersError('Could not load players.'))
      .finally(() => setPlayersLoading(false));
  }, [isOpen, leagueId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  const handleClose = () => {
    setStep('browse');
    setSearchTerm('');
    setPositionFilter('all');
    setPendingAdd(null);
    setSelectedDropId(null);
    setSwapError(null);
    onClose();
  };

  const handleAddClick = (player: Player) => {
    if (rosterFull) {
      setPendingAdd(player);
      setSelectedDropId(null);
      setSwapError(null);
      setStep('confirm-drop');
    } else {
      executeSwap(player.id, undefined);
    }
  };

  const executeSwap = async (addId: number, dropId?: number) => {
    setSwapping(true);
    setSwapError(null);
    try {
      await onSwapPlayer(addId, dropId);
      handleClose();
    } catch (err: any) {
      setSwapError(err.message || 'Failed to add player.');
      setSwapping(false);
    }
  };

  // Filter and sort players for the browse step
  const filteredPlayers = players
    .filter(p => {
      const roster = leagueId ? [] : currentRoster; // if leagueId, server already filtered
      if (!leagueId && roster.some(r => r.id === p.id)) return false;
      if (positionFilter !== 'all' && p.position !== positionFilter) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const team = p.nfl_team || p.nflTeam;
        return (
          p.name.toLowerCase().includes(s) ||
          (team?.abbreviation || '').toLowerCase().includes(s) ||
          (team?.name || '').toLowerCase().includes(s)
        );
      }
      return true;
    })
    .sort((a, b) =>
      sortBy === 'name'
        ? a.name.localeCompare(b.name)
        : bySeasonAverageDesc(a, b)
    );

  // Drop candidates for confirm-drop step: same position as pendingAdd
  const dropCandidates = pendingAdd
    ? currentRoster.filter(p => p.position === pendingAdd.position)
    : [];

  const getPositionColor = (pos: string) =>
    pos === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800';

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">
              {step === 'confirm-drop' ? 'Drop a Player to Make Room' : 'Add Player'}
            </h2>
            <p className="text-sm text-secondary-500 mt-0.5">
              {step === 'confirm-drop' && pendingAdd
                ? `Adding ${pendingAdd.name} · select a ${pendingAdd.position === 'K' ? 'kicker' : 'punter'} to drop`
                : `${currentRoster.length} / 6 roster spots filled`}
            </p>
          </div>
          <button onClick={handleClose} className="icon-btn text-secondary-400 hover:text-secondary-600 rounded -mr-2">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {step === 'browse' ? (
          <>
            {/* Search + filters */}
            <div className="px-6 py-3 border-b border-secondary-200 shrink-0">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                  <input
                    type="text"
                    placeholder="Search players..."
                    className="form-input pl-9 py-2 text-sm w-full"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
                <select
                  className="form-select py-2 text-sm w-36"
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as any)}
                >
                  <option value="fantasy_points_avg">Avg pts</option>
                  <option value="name">Name</option>
                </select>
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className="btn-outline-secondary text-sm flex items-center gap-1.5 py-2"
                >
                  <FunnelIcon className="w-4 h-4" />
                  Filter
                  {positionFilter !== 'all' && <span className="w-1.5 h-1.5 rounded-full bg-primary-600" />}
                </button>
              </div>
              {showFilters && (
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-secondary-100">
                  <span className="text-xs text-secondary-500 font-medium">Position</span>
                  {(['all', 'K', 'P'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setPositionFilter(p)}
                      className={`px-3 py-2 rounded text-xs font-medium transition-colors ${
                        positionFilter === p
                          ? 'bg-primary-600 text-white'
                          : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                      }`}
                    >
                      {p === 'all' ? 'All' : p === 'K' ? 'Kickers' : 'Punters'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Player list */}
            <div className="flex-1 overflow-y-auto">
              {playersLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="loading-spinner w-5 h-5 mr-3" />
                  <span className="text-secondary-600 text-sm">Loading free agents...</span>
                </div>
              ) : playersError ? (
                <div className="flex items-center justify-center py-16 text-center px-6">
                  <div>
                    <p className="text-danger-600 text-sm mb-3">{playersError}</p>
                    <button onClick={fetchPlayers} className="btn-outline-primary btn-sm">Retry</button>
                  </div>
                </div>
              ) : filteredPlayers.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-secondary-500 text-sm">
                  No players found
                </div>
              ) : (
                <div className="divide-y divide-secondary-100">
                  {filteredPlayers.map(player => {
                    const nflTeam = player.nfl_team || player.nflTeam;
                    const season = seasonAverage(player);
                    return (
                      <div key={player.id} className="flex items-center gap-4 px-6 py-3 hover:bg-secondary-50">
                        {/* Avatar */}
                        <div className="relative w-10 h-10 shrink-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${getPositionColor(player.position)}`}>
                            {player.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                          </div>
                          {(player.headshot_url || player.espn_id) && (
                            <img
                              src={player.headshot_url || `https://a.espncdn.com/i/headshots/nfl/players/full/${player.espn_id}.png`}
                              alt={player.name}
                              className="absolute inset-0 w-10 h-10 rounded-full object-cover object-top"
                              onError={e => { e.currentTarget.style.display = 'none'; }}
                            />
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-secondary-900 text-sm">{player.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${getPositionColor(player.position)}`}>
                              {player.position}
                            </span>
                            <span className="text-xs text-secondary-400">{nflTeam?.abbreviation || 'FA'}</span>
                            {nflTeam?.stadium_type === 'dome' && (
                              <BuildingOffice2Icon className="w-3.5 h-3.5 text-secondary-400" title="Dome" />
                            )}
                          </div>
                          <div className="text-xs text-secondary-500 flex items-center gap-1 mt-0.5">
                            <TrophyIcon className="w-3.5 h-3.5" />
                            <span>{season.display} pts · {season.label}</span>
                            {isElite(season) && <StarIconSolid className="w-3 h-3 text-warning-400" title="Elite" />}
                          </div>
                        </div>
                        {/* Add button */}
                        <button
                          onClick={() => handleAddClick(player)}
                          disabled={swapping}
                          className="btn-primary btn-sm flex items-center gap-1 shrink-0"
                        >
                          <PlusIcon className="w-3.5 h-3.5" />
                          {rosterFull ? 'Swap' : 'Add'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-secondary-100 bg-secondary-50 shrink-0">
              {swapError && (
                <p className="text-xs text-danger-600 mb-1.5">{swapError}</p>
              )}
              <p className="text-xs text-secondary-500">
                {leagueId
                  ? 'Showing free agents — players not rostered by any team in this league.'
                  : 'Showing all active NFL kickers and punters.'}
                {rosterFull && ' Roster full — adding a player will require dropping one of the same position.'}
              </p>
            </div>
          </>
        ) : (
          /* confirm-drop step */
          <div className="flex-1 overflow-y-auto">
            {/* Player being added */}
            {pendingAdd && (
              <div className="px-6 py-4 bg-primary-50 border-b border-primary-100">
                <p className="text-xs font-medium text-primary-700 mb-2">Adding to your roster:</p>
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10 shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${getPositionColor(pendingAdd.position)}`}>
                      {pendingAdd.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </div>
                    {(pendingAdd.headshot_url || pendingAdd.espn_id) && (
                      <img
                        src={pendingAdd.headshot_url || `https://a.espncdn.com/i/headshots/nfl/players/full/${pendingAdd.espn_id}.png`}
                        alt={pendingAdd.name}
                        className="absolute inset-0 w-10 h-10 rounded-full object-cover object-top"
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <div>
                    <span className="font-semibold text-secondary-900">{pendingAdd.name}</span>
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded font-medium ${getPositionColor(pendingAdd.position)}`}>
                      {pendingAdd.position}
                    </span>
                    <span className="ml-1.5 text-xs text-secondary-500">
                      {(pendingAdd.nfl_team || pendingAdd.nflTeam)?.abbreviation} · {seasonAverage(pendingAdd).display} pts · {seasonAverage(pendingAdd).label}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Drop candidates */}
            <div className="px-6 py-4">
              {dropCandidates.length === 0 ? (
                <p className="text-sm text-secondary-600 py-4 text-center">
                  No {pendingAdd?.position === 'K' ? 'kickers' : 'punters'} on your roster to drop.
                </p>
              ) : (
                <>
                  <p className="text-sm text-secondary-600 mb-3">
                    Select a {pendingAdd?.position === 'K' ? 'kicker' : 'punter'} to drop:
                  </p>
                  <div className="space-y-2">
                    {dropCandidates.map(player => {
                      const nflTeam = player.nfl_team || player.nflTeam;
                      const season = seasonAverage(player);
                      const selected = selectedDropId === player.id;
                      return (
                        <button
                          key={player.id}
                          onClick={() => setSelectedDropId(player.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-colors text-left ${
                            selected
                              ? 'border-danger-400 bg-danger-50'
                              : 'border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50'
                          }`}
                        >
                          <div className="relative w-9 h-9 shrink-0">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${getPositionColor(player.position)}`}>
                              {player.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                            </div>
                            {(player.headshot_url || player.espn_id) && (
                              <img
                                src={player.headshot_url || `https://a.espncdn.com/i/headshots/nfl/players/full/${player.espn_id}.png`}
                                alt={player.name}
                                className="absolute inset-0 w-9 h-9 rounded-full object-cover object-top"
                                onError={e => { e.currentTarget.style.display = 'none'; }}
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-secondary-900 text-sm">{player.name}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${getPositionColor(player.position)}`}>
                                {player.position}
                              </span>
                              <span className="text-xs text-secondary-400">{nflTeam?.abbreviation}</span>
                            </div>
                            <div className="text-xs text-secondary-500">{season.display} pts · {season.label}</div>
                          </div>
                          {selected && <span className="text-xs font-medium text-danger-600 shrink-0">Drop</span>}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              {swapError && <p className="text-sm text-danger-600 mt-3">{swapError}</p>}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-secondary-200 bg-secondary-50 shrink-0 flex justify-between items-center">
              <button
                onClick={() => { setStep('browse'); setSwapError(null); }}
                className="btn-outline-secondary"
                disabled={swapping}
              >
                ← Back
              </button>
              <button
                onClick={() => pendingAdd && selectedDropId && executeSwap(pendingAdd.id, selectedDropId)}
                disabled={!selectedDropId || swapping || dropCandidates.length === 0}
                className="btn-primary flex items-center gap-2"
              >
                {swapping ? (
                  <><div className="loading-spinner w-4 h-4" />Processing...</>
                ) : (
                  'Confirm Swap'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
